import { faBan, faCheck, faPen, faPlus, faSliders, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAtom } from "@dbeining/react-atom";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Alert, Button, Card, Container, Form, Modal, Table } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { userAtom } from "../../atoms";
import {
    ChangeDefaultsSiteModal,
    EwcRuleLookupDisplay,
    applyEwcRuleDraftAction,
    applyPageModelToEwcRuleDrafts,
    buildEwcRuleDraftFromModal,
    computeGeneralDefaultPreview,
    ewcRuleTargetShowsResponsiveDescription,
    canMutateWasteMovements,
    filterDefaultsEligibleSites,
    getAvailableEwcOptionsForRuleModal,
    resolveWasteMovementFormAccess,
    getEwcRuleTriggerDisplays,
    getOrderedEwcRuleTargetEntries,
    hasUnsavedWasteMovementDefaultsChanges,
    isEwcRuleTargetCodeOnlySelectField,
    isGeneralDefaultCodeOnlySelectField,
    mostFrequentPreviewFromApi,
    persistWasteMovementDefaults,
    resolvedPreviewLabel,
    resolveWasteMovementDefaultsSiteId,
    upsertEwcRuleDraft,
    useWasteMovementReferenceLookups,
    type EwcRuleDraft,
} from "@/features/wasteMovement";
import { UnsavedChangesGuardProvider, useUnsavedChangesGuardContext } from "@/features/unsavedChanges";
import { formatEwcDisplayCode } from "../../components/Forms/EwcCodeInput";
import { RegLookupSelect, type RegLookupSelectItem } from "../../components/Forms/RegLookupSelect";
import { EditFormActions } from "../../components/Forms";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import type { ISiteOption } from "../../customTypings/customer/Customer";
import type {
    IWasteMovementDefaultsPageModel,
    WasteMovementDefaultMode,
} from "@/customTypings/wasteMovement/WasteMovementDefaults";
import type { IRegMunicipalDegradableLookupItem, WasteMovementDirection } from "@/customTypings/wasteMovement/WasteMovement";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { useUnsavedChangesBlocker } from "@/features/unsavedChanges";
import { wasteMovementDefaultsService } from "../../services/wasteMovementDefaults/wasteMovementDefaults.service";
import { sitesService } from "../../services/sites/sites.service";
import {
    getUserFacingErrorMessage,
    logErrorForDev,
    SectionLabels,
    showToastError,
    showToastSuccess,
} from "../../utils";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";

const EWC_TARGET_RECEIVED = [
    "RegDisposalRecoveryCodeId",
    "MunicipalFlagId",
    "RegPhysicalStateId",
    "DegradableFlagId",
    "RegSourceActivityId",
    "RegPreTreatmentId",
] as const;

const EWC_TARGET_REMOVED = ["RegDisposalRecoveryCodeId", "MunicipalFlagId", "RegPhysicalStateId"] as const;

function WasteMovementDefaultsRouteBlocker({ when }: { when: boolean }) {
    useUnsavedChangesBlocker({ when });
    return null;
}

function WasteMovementDefaultsGuardedProceed({
    children,
}: {
    children: (requestProceed: (proceed: () => void) => void) => ReactNode;
}) {
    const ctx = useUnsavedChangesGuardContext();
    if (!ctx) return null;
    return <>{children(ctx.requestProceed)}</>;
}

export type WasteMovementDefaultsPageConfig = {
    movementDirection: WasteMovementDirection;
    listTitle: string;
    listPath: string;
    defaultsPath: string;
    siteStorageKey: string;
};

type GeneralFieldDraft = {
    fieldKey: string;
    label: string;
    mode: WasteMovementDefaultMode;
    specificGuidValue: string;
    previewStatus: string;
    previewResolvedDisplay?: string | null;
};

function mapMunicipalDegradableToRegLookupItems(
    items: ReadonlyArray<IRegMunicipalDegradableLookupItem>
): RegLookupSelectItem[] {
    return items.map((item) => ({
        id: item.id,
        code: item.applies,
        description: item.description,
    }));
}

function applyPageModelToDrafts(
    model: IWasteMovementDefaultsPageModel,
    lookupItemsForField: (fieldKey: string) => ReadonlyArray<RegLookupSelectItem>
): GeneralFieldDraft[] {
    return model.generalDefaults.map((f) => {
        const preview = computeGeneralDefaultPreview(
            {
                fieldKey: f.fieldKey,
                mode: f.mode,
                specificGuidValue: f.specificGuidValue ?? "",
            },
            lookupItemsForField(f.fieldKey),
            { mode: f.mode, status: f.status, resolvedDisplay: f.resolvedDisplay }
        );
        return {
            fieldKey: f.fieldKey,
            label: f.label,
            mode: f.mode,
            specificGuidValue: f.specificGuidValue ?? "",
            previewStatus: preview.status,
            previewResolvedDisplay: preview.resolvedDisplay,
        };
    });
}

export function WasteMovementDefaultsPage({ config }: { config: WasteMovementDefaultsPageConfig }) {
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const roleCanMutate = canMutateWasteMovements(roleNames);
    const roleAccess = resolveWasteMovementFormAccess({
        isNew: true,
        roleNames,
        entityLabel: config.listTitle,
        reportingEligibleSites: [],
    });
    const { lookups, error: lookupError } = useWasteMovementReferenceLookups();

    const [sites, setSites] = useState<ISiteOption[]>([]);
    const [siteId, setSiteId] = useState<string>("");
    const [siteName, setSiteName] = useState<string>("");
    const [pageModel, setPageModel] = useState<IWasteMovementDefaultsPageModel | null>(null);
    const [generalDrafts, setGeneralDrafts] = useState<GeneralFieldDraft[]>([]);
    const [ewcRuleDrafts, setEwcRuleDrafts] = useState<EwcRuleDraft[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [ruleModalOpen, setRuleModalOpen] = useState(false);
    const [changeSiteOpen, setChangeSiteOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<EwcRuleDraft | null>(null);

    const [ruleEwcIds, setRuleEwcIds] = useState<string[]>([]);
    const [ruleTargets, setRuleTargets] = useState<Record<string, string>>({});

    const formAutoFocusRef = useFormAutoFocus<HTMLDivElement>(!loading && !!pageModel && !loadError, [
        loading,
        pageModel,
        loadError,
    ]);

    const ewcTargetKeys = useMemo(
        () => (config.movementDirection === "Received" ? EWC_TARGET_RECEIVED : EWC_TARGET_REMOVED),
        [config.movementDirection]
    );

    const defaultsEligibleSites = useMemo(() => filterDefaultsEligibleSites(sites), [sites]);

    const loadPage = useCallback(
        async (resolvedSiteId: string) => {
            setLoading(true);
            setLoadError(null);
            try {
                const model = await wasteMovementDefaultsService.getPage(resolvedSiteId, config.movementDirection);
                setPageModel(model);
            } catch (e: unknown) {
                setLoadError(getUserFacingErrorMessage(e, "Could not load your defaults."));
            } finally {
                setLoading(false);
            }
        },
        [config.movementDirection]
    );

    useEffect(() => {
        let cancelled = false;

        sitesService
            .listForOrganisation()
            .then((list) => {
                if (cancelled) return;

                setSites(list);
                const storedSiteId = localStorage.getItem(config.siteStorageKey);
                const resolved = resolveWasteMovementDefaultsSiteId(list, { storedSiteId });
                if (!resolved) {
                    setLoadError("No sites are available for your defaults.");
                    setLoading(false);
                    return;
                }
                setSiteId(resolved);
                const site = filterDefaultsEligibleSites(list).find((s) => s.id === resolved);
                setSiteName(site?.siteName ?? "");
                localStorage.setItem(config.siteStorageKey, resolved);
                void loadPage(resolved);
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                logErrorForDev(`${config.defaultsPath}.sites`, e);
                setLoadError("Could not load sites.");
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [config.defaultsPath, config.siteStorageKey, loadPage]);

    const handleChangeSite = useCallback(
        (newSiteId: string) => {
            setSiteId(newSiteId);
            const site = defaultsEligibleSites.find((s) => s.id === newSiteId);
            setSiteName(site?.siteName ?? "");
            localStorage.setItem(config.siteStorageKey, newSiteId);
            void loadPage(newSiteId);
        },
        [config.siteStorageKey, defaultsEligibleSites, loadPage]
    );

    const saveDefaults = useCallback(async (): Promise<boolean> => {
        if (!siteId || !pageModel) return false;
        setSaving(true);
        try {
            const model = await persistWasteMovementDefaults(
                siteId,
                config.movementDirection,
                generalDrafts.map((d) => ({
                    fieldKey: d.fieldKey,
                    defaultMode: d.mode,
                    specificGuidValue: d.mode === "SpecificValue" ? d.specificGuidValue || null : null,
                })),
                ewcRuleDrafts,
                pageModel.ewcRules
            );
            setPageModel(model);
            showToastSuccess("Your defaults were saved.");
            return true;
        } catch (e: unknown) {
            showToastError(getUserFacingErrorMessage(e, "Could not save your defaults."));
            return false;
        } finally {
            setSaving(false);
        }
    }, [siteId, pageModel, config.movementDirection, generalDrafts, ewcRuleDrafts]);

    const isDefaultsDirty = useMemo(
        () => hasUnsavedWasteMovementDefaultsChanges(generalDrafts, ewcRuleDrafts, pageModel),
        [generalDrafts, ewcRuleDrafts, pageModel]
    );

    const lookupItemsForField = useCallback(
        (fieldKey: string) => {
            if (!lookups) return [];
            switch (fieldKey) {
                case "RegEwcCodeId":
                    return lookups.ewcs;
                case "RegDisposalRecoveryCodeId":
                    return lookups.disposalRecoveryCodes;
                case "RegPhysicalStateId":
                    return lookups.physicalStates;
                case "RegSourceActivityId":
                    return lookups.sourceActivities;
                case "RegPreTreatmentId":
                    return lookups.preTreatments;
                case "MunicipalFlagId":
                    return mapMunicipalDegradableToRegLookupItems(
                        lookups.municipalDegradables.filter((m) => m.indicatorType === "Municipal")
                    );
                case "DegradableFlagId":
                    return mapMunicipalDegradableToRegLookupItems(
                        lookups.municipalDegradables.filter((m) => m.indicatorType === "Degradable")
                    );
                default:
                    return [];
            }
        },
        [lookups]
    );

    useEffect(() => {
        if (!pageModel || !lookups) return;
        setGeneralDrafts(applyPageModelToDrafts(pageModel, lookupItemsForField));
        setEwcRuleDrafts(applyPageModelToEwcRuleDrafts(pageModel.ewcRules));
    }, [pageModel, lookups, lookupItemsForField]);

    const fetchMostFrequentPreview = useCallback(
        async (fieldKey: string) => {
            if (!siteId || !lookups) return;
            try {
                const result = await wasteMovementDefaultsService.getMostFrequentPreview(
                    siteId,
                    config.movementDirection,
                    fieldKey
                );
                const preview = mostFrequentPreviewFromApi(
                    fieldKey,
                    result.status,
                    lookupItemsForField(fieldKey),
                    result.resolvedGuidValue,
                    result.resolvedDisplay
                );
                setGeneralDrafts((prev) =>
                    prev.map((d) => {
                        if (d.fieldKey !== fieldKey || d.mode !== "MostFrequent") return d;
                        return {
                            ...d,
                            previewStatus: preview.status,
                            previewResolvedDisplay: preview.resolvedDisplay,
                        };
                    })
                );
            } catch (e: unknown) {
                logErrorForDev(`${config.defaultsPath}.mostFrequent.${fieldKey}`, e);
            }
        },
        [siteId, lookups, config.movementDirection, config.defaultsPath, lookupItemsForField]
    );

    const fieldLabelByKey = useMemo(
        () => Object.fromEntries(generalDrafts.map((d) => [d.fieldKey, d.label])),
        [generalDrafts]
    );

    const labelForEwcTargetField = useCallback(
        (fieldKey: string) => fieldLabelByKey[fieldKey] ?? fieldKey,
        [fieldLabelByKey]
    );

    const availableModalEwcs = useMemo(
        () =>
            getAvailableEwcOptionsForRuleModal(
                lookups?.ewcs ?? [],
                ewcRuleDrafts,
                ruleEwcIds,
                editingRule?.id
            ),
        [lookups?.ewcs, ewcRuleDrafts, ruleEwcIds, editingRule?.id]
    );

    const updateGeneralDraft = useCallback(
        (fieldKey: string, patch: Partial<GeneralFieldDraft>) => {
            if (!lookups) return;
            setGeneralDrafts((prev) =>
                prev.map((d) => {
                    if (d.fieldKey !== fieldKey) return d;
                    const next = { ...d, ...patch };
                    const serverField = pageModel?.generalDefaults.find((f) => f.fieldKey === fieldKey);
                    const preview = computeGeneralDefaultPreview(
                        next,
                        lookupItemsForField(fieldKey),
                        serverField
                            ? {
                                  mode: serverField.mode,
                                  status: serverField.status,
                                  resolvedDisplay: serverField.resolvedDisplay,
                              }
                            : null
                    );
                    return {
                        ...next,
                        previewStatus: preview.status,
                        previewResolvedDisplay: preview.resolvedDisplay,
                    };
                })
            );
            if (patch.mode === "MostFrequent") {
                void fetchMostFrequentPreview(fieldKey);
            }
        },
        [lookups, lookupItemsForField, pageModel, fetchMostFrequentPreview]
    );

    const handleSave = async () => {
        await saveDefaults();
    };

    const openRuleModal = (rule?: EwcRuleDraft) => {
        setEditingRule(rule ?? null);
        setRuleEwcIds(rule?.triggerEwcIds ?? []);
        setRuleTargets(rule ? { ...rule.targetValues } : {});
        setRuleModalOpen(true);
    };

    const readOnlyForm = !roleCanMutate || pageModel?.canMutate === false;

    const handleApplyRuleDraft = () => {
        if (!lookups || readOnlyForm) return;
        const draft = buildEwcRuleDraftFromModal(
            editingRule,
            ruleEwcIds,
            ruleTargets,
            lookups.ewcs,
            lookupItemsForField
        );
        setEwcRuleDrafts((prev) => upsertEwcRuleDraft(prev, draft));
        setRuleModalOpen(false);
    };

    const runRuleAction = (action: "enable" | "disable" | "softdelete", ruleId: string) => {
        if (readOnlyForm) return;
        setEwcRuleDrafts((prev) => applyEwcRuleDraftAction(prev, ruleId, action));
    };

    if (!roleCanMutate) {
        return (
            <Container fluid className="py-4">
                <Helmet>
                    <title>Your {config.listTitle} Defaults</title>
                </Helmet>
                <Alert variant="warning">{roleAccess.permissionMessage}</Alert>
                <Link to={config.listPath}>Back to {config.listTitle}</Link>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="py-4">
                <LoadingSpinner />
            </Container>
        );
    }

    if (loadError || !lookups) {
        return (
            <Container fluid className="py-4">
                <Alert variant="danger">{loadError ?? lookupError ?? "Could not load reference data."}</Alert>
                <Link to={config.listPath}>Back to {config.listTitle}</Link>
            </Container>
        );
    }

    const activeRules = ewcRuleDrafts.filter((r) => !r.isDeleted);
    const pageTitle = `Your ${config.listTitle} Defaults`;

    return (
        <UnsavedChangesGuardProvider
            isDirty={!readOnlyForm && isDefaultsDirty}
            onSave={saveDefaults}
            saving={saving}
        >
            <WasteMovementDefaultsRouteBlocker when={!readOnlyForm && isDefaultsDirty} />
            <WasteMovementDefaultsGuardedProceed>
                {(requestProceed) => {
                    const handleChangeSiteSelect = (newSiteId: string) => {
                        setChangeSiteOpen(false);
                        if (newSiteId === siteId) return;
                        requestProceed(() => handleChangeSite(newSiteId));
                    };

                    const handleCancel = () => {
                        requestProceed(() => navigate(config.listPath));
                    };

                    return (
        <Container fluid ref={formAutoFocusRef}>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                items={[
                    { text: SectionLabels.operations, to: "/operations" },
                    { text: config.listTitle, to: config.listPath },
                    { text: "Your Defaults" },
                ]}
            />
            <div
                className="d-flex flex-wrap align-items-center gap-2 mb-3 w-100"
                style={{ borderBottom: "2px solid var(--primary)", paddingBottom: "0.5rem" }}
            >
                <h1 className="mb-0 flex-grow-1 min-w-0" style={{ borderBottom: "none", paddingBottom: 0 }}>
                    {pageTitle}
                </h1>
            </div>
            <p className="text-muted">
                Configure default lookup values for new {config.listTitle.toLowerCase()} records. Date, reference, origin/destination, amount, and notes are not configurable here.
            </p>
            {readOnlyForm && pageModel?.siteReportingActiveFlag === false && (
                <Alert variant="warning" className="mb-3">
                    This site is not active for reporting. Operational waste movements cannot be created or changed until reporting is re-enabled.
                </Alert>
            )}

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <h5 className="text-muted mb-0">Showing defaults for the {siteName} Site</h5>
                {defaultsEligibleSites.length > 1 && !readOnlyForm && (
                    <Button variant="secondary" onClick={() => setChangeSiteOpen(true)}>
                        Change Site
                    </Button>
                )}
            </div>

            <ChangeDefaultsSiteModal
                show={changeSiteOpen}
                currentSiteId={siteId}
                eligibleSites={defaultsEligibleSites}
                onCancel={() => setChangeSiteOpen(false)}
                onSelect={handleChangeSiteSelect}
            />

            <Card className="mb-4">
                <Card.Header>General Defaults</Card.Header>
                <Card.Body>
                    <Table
                        responsive
                        className="align-middle mb-0"
                        style={{ tableLayout: "fixed", width: "100%", minWidth: 800 }}
                    >
                        <colgroup>
                            <col style={{ width: 200 }} />
                            <col style={{ width: 200 }} />
                            <col />
                            <col style={{ width: 200 }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Field</th>
                                <th>Mode</th>
                                <th>Specific value</th>
                                <th>Resolves to</th>
                            </tr>
                        </thead>
                        <tbody>
                            {generalDrafts.map((row) => (
                                <tr key={row.fieldKey}>
                                    <td>{row.label}</td>
                                    <td>
                                        <Form.Select
                                            className="w-100"
                                            value={row.mode}
                                            disabled={readOnlyForm}
                                            onChange={(e) =>
                                                updateGeneralDraft(row.fieldKey, {
                                                    mode: e.target.value as WasteMovementDefaultMode,
                                                    specificGuidValue:
                                                        e.target.value === "SpecificValue" ? row.specificGuidValue : "",
                                                })
                                            }
                                        >
                                            <option value="None">No Default</option>
                                            <option value="SpecificValue">Specific Value</option>
                                            <option value="MostFrequent">Most Frequent</option>
                                        </Form.Select>
                                    </td>
                                    <td>
                                        {row.mode === "SpecificValue" ? (
                                            <RegLookupSelect
                                                value={row.specificGuidValue}
                                                onChange={(id) =>
                                                    updateGeneralDraft(row.fieldKey, { specificGuidValue: id })
                                                }
                                                items={lookupItemsForField(row.fieldKey)}
                                                placeholder="Select…"
                                                disabled={readOnlyForm}
                                                portMenu
                                                fillContainer
                                                formatCode={
                                                    row.fieldKey === "RegEwcCodeId" ? formatEwcDisplayCode : undefined
                                                }
                                                codeOnly={isGeneralDefaultCodeOnlySelectField(row.fieldKey)}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="small">
                                            {resolvedPreviewLabel(row.previewStatus, row.previewResolvedDisplay)}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>EWC Defaults</span>
                    {!readOnlyForm && (
                        <Button size="sm" variant="primary" onClick={() => openRuleModal()}>
                            <FontAwesomeIcon icon={faPlus} className="me-1" /> Add rule
                        </Button>
                    )}
                </Card.Header>
                <Card.Body>
                    {activeRules.length === 0 ? (
                        <p className="text-muted mb-0">No EWC rules configured.</p>
                    ) : (
                        activeRules.map((rule) => {
                            const triggerDisplays = getEwcRuleTriggerDisplays(rule);
                            const targetEntries = getOrderedEwcRuleTargetEntries(
                                rule,
                                ewcTargetKeys,
                                labelForEwcTargetField
                            );

                            return (
                            <div key={rule.id} className="border rounded p-3 mb-3">
                                <div className="d-flex flex-nowrap justify-content-between align-items-start gap-2 mb-2">
                                    <div className="d-flex gap-2 small min-w-0 pe-2">
                                        {triggerDisplays.length > 0 && (
                                            <>
                                                <div className="fw-bold text-feature-secondary flex-shrink-0">
                                                    When EWC is:
                                                </div>
                                                <div className="min-w-0">
                                                    {triggerDisplays.map((display, index) => (
                                                        <div key={`${rule.id}-ewc-${index}`}>
                                                            <EwcRuleLookupDisplay
                                                                display={display}
                                                                formatCode={formatEwcDisplayCode}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="d-flex gap-1 flex-shrink-0">
                                        {!readOnlyForm && !rule.isEnabled && !rule.isDeleted && (
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                className="d-inline-flex align-items-center justify-content-center px-2"
                                                aria-label="Enable rule"
                                                title="Enable rule"
                                                onClick={() => runRuleAction("enable", rule.id)}
                                            >
                                                <FontAwesomeIcon icon={faCheck} size="sm" aria-hidden />
                                            </Button>
                                        )}
                                        {!readOnlyForm && rule.isEnabled && (
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                className="d-inline-flex align-items-center justify-content-center px-2"
                                                aria-label="Disable rule"
                                                title="Disable rule"
                                                onClick={() => runRuleAction("disable", rule.id)}
                                            >
                                                <FontAwesomeIcon icon={faBan} size="sm" aria-hidden />
                                            </Button>
                                        )}
                                        {!readOnlyForm && (
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                className="d-inline-flex align-items-center justify-content-center px-2"
                                                aria-label="Edit rule"
                                                title="Edit rule"
                                                onClick={() => openRuleModal(rule)}
                                            >
                                                <FontAwesomeIcon icon={faPen} size="sm" aria-hidden />
                                            </Button>
                                        )}
                                        {!readOnlyForm && (
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                className="d-inline-flex align-items-center justify-content-center px-2"
                                                aria-label="Delete rule"
                                                title="Delete rule"
                                                onClick={() => runRuleAction("softdelete", rule.id)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} size="sm" aria-hidden />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <ul className="mb-0 small list-unstyled">
                                    {targetEntries.map(({ fieldKey, label, value }) => (
                                        <li key={fieldKey}>
                                            <span className="fw-bold text-feature-secondary">{label}:</span>{" "}
                                            {ewcRuleTargetShowsResponsiveDescription(fieldKey) ? (
                                                <EwcRuleLookupDisplay display={value} />
                                            ) : (
                                                value
                                            )}
                                        </li>
                                    ))}
                                </ul>
                                {!rule.isEnabled && <div className="small text-warning mt-1">Rule is disabled.</div>}
                            </div>
                            );
                        })
                    )}
                </Card.Body>
            </Card>

            <EditFormActions>
                {!readOnlyForm && (
                    <Button variant="primary" type="button" disabled={saving} onClick={() => void handleSave()}>
                        {saving ? <LoadingSpinner text="Saving…" /> : "Save"}
                    </Button>
                )}
                <Button variant="outline-secondary" type="button" onClick={handleCancel}>
                    {readOnlyForm ? "Back" : "Cancel"}
                </Button>
            </EditFormActions>

            <Modal show={ruleModalOpen} onHide={() => setRuleModalOpen(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingRule ? "Edit EWC rule" : "New EWC rule"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>When EWC code is</Form.Label>
                        <RegLookupSelect
                            value={ruleEwcIds[0] ?? ""}
                            onChange={(id) => {
                                if (id && !ruleEwcIds.includes(id)) setRuleEwcIds([...ruleEwcIds, id]);
                            }}
                            items={availableModalEwcs}
                            placeholder="Select EWC…"
                            portMenu
                            formatCode={formatEwcDisplayCode}
                        />
                        <div className="d-flex flex-wrap gap-1 mt-2">
                            {ruleEwcIds.map((id) => {
                                const item = lookups.ewcs.find((e) => e.id === id);
                                return (
                                    <Button
                                        key={id}
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => setRuleEwcIds(ruleEwcIds.filter((x) => x !== id))}
                                    >
                                        {(item ? formatEwcDisplayCode(item.code) : id) + " ×"}
                                    </Button>
                                );
                            })}
                        </div>
                    </Form.Group>
                    {ewcTargetKeys.map((fieldKey) => {
                        const label = labelForEwcTargetField(fieldKey);
                        return (
                            <Form.Group className="mb-3" key={fieldKey}>
                                <Form.Label>{label}</Form.Label>
                                <RegLookupSelect
                                    value={ruleTargets[fieldKey] ?? ""}
                                    onChange={(id) => setRuleTargets({ ...ruleTargets, [fieldKey]: id })}
                                    items={lookupItemsForField(fieldKey)}
                                    placeholder="Select…"
                                    portMenu
                                    codeOnly={isEwcRuleTargetCodeOnlySelectField(fieldKey)}
                                />
                            </Form.Group>
                        );
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setRuleModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleApplyRuleDraft}>
                        {editingRule ? "Update rule" : "Add rule"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
                    );
                }}
            </WasteMovementDefaultsGuardedProceed>
        </UnsavedChangesGuardProvider>
    );
}

export { faSliders };
